import { createSelector } from 'reselect';
import { hasPermission } from 'utils/common';
import { PermissionsEnum } from 'utils/permissions.enum';
import { extractTokenPayload } from 'utils/token';
import { isNil, get, size, find, filter } from 'lodash-es';

/**
 * @param {*} state
 * @return {*}
 */
export const displaySettingsSelector = createSelector(
  (state) => state.d_settings.display,
  (d) => d
);

export const assetsSelector = createSelector(displaySettingsSelector, (d) => ({
  faviconImage: find(d.assets, (item) => item.type === 'faviconImage'),
  mainCss: find(d.assets, (item) => item.type === 'mainCss'),
}));

/**
 * @param {*} state
 * @return channel[]
 */
export const channelOverviewSelector = createSelector(displaySettingsSelector, (display) => display.channelPage);

/**
 * @param {*} state
 * @return channel[]
 */
export const homePageSelector = createSelector(displaySettingsSelector, (display) => display.homePage);

/**
 * @param {*} state
 * @return sortedList[]
 */
export const sortedVideosSelector = createSelector(homePageSelector, (homePage) =>
  filter(get(homePage, 'videoLists'), (video) => !get(video, 'id'))
);

/**
 * @param {*} state
 * @return {string} startVideoId
 */
export const homePageStartVideoSelector = createSelector(homePageSelector, (homePage) => get(homePage, 'startVideoId'));

/**
 * @param {*} state
 * @return nabBar{}
 */
export const nabBarSelector = createSelector(displaySettingsSelector, (display) => display.navBar);

/**
 * @param {*} state
 * @return videoLists[]
 */
export const navBarRootChannelsSelector = createSelector(nabBarSelector, (nabBar) => nabBar.videoLists);

export const startChannelsSelector = createSelector(displaySettingsSelector, (displaySettings) =>
  displaySettings ? displaySettings.startChannels : null
);
/**
 * @return {channelModel[]}
 */
export const isLoadingSelector = createSelector(displaySettingsSelector, (displaySettings) => displaySettings === null);

/**
 * @param {*} state
 * @return boolean
 */
export const helpMenuSelector = createSelector(displaySettingsSelector, (displaySettings) =>
  size(displaySettings.helpMenu) ? displaySettings.helpMenu : null
);
/**
 * @param {*} state
 * @return boolean
 */
export const getVideosPerPageSelector = createSelector(
  displaySettingsSelector,
  (displaySettings) => displaySettings.videosPerPage
);

export const getShowVideoUploaderSelector = createSelector(displaySettingsSelector, (display) =>
  get(display, 'video.additionalData.uploader', false)
);

export const getVideoDisplaySettingsSelector = createSelector(displaySettingsSelector, (display) =>
  get(display, 'video.additionalData')
);

export const getFooterElements = createSelector(displaySettingsSelector, (display) => display.footerElements);

/* ### Features toggle selectors ### */

export const getFeaturesSelector = createSelector(displaySettingsSelector, (display) => display.features);

export const getUploadFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'Upload.enabled', false)
);

export const getRecordVideoFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'RecordVideo.enabled', false)
);

export const getUploadPodcastFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'UploadPodcast.enabled', false)
);

export const getRecordScreenFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'RecordScreen.enabled', false)
);

export const getRelatedVideosFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'RelatedVideos.enabled', false)
);

export const getSavedVideosFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'SavedVideos.enabled', false)
);

export const getRatingFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'Rating.enabled', false)
);

export const getRatingSortingFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'Rating.sortingEnabled', false)
);

export const getShareViaEmailSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ShareViaEmail.enabled', false)
);

export const getShareViaTwitterSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ShareViaTwitter.enabled', false)
);

export const getShareViaYammerSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ShareViaYammer')
);

export const getShareViaMicrosoftTeamsSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ShareViaMicrosoftTeams')
);

export const getShareViaFacebookSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ShareViaFacebook')
);

export const getShareDirectLinkSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ShareDirectLink.enabled')
);

export const commentsFeatureSelector = createSelector(getFeaturesSelector, (features) => get(features, 'Comments'));

export const withCommentsFeatureSelector = createSelector(commentsFeatureSelector, (comments) =>
  get(comments, 'enabled', false)
);

export const getIqsFeatureSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'Iqs.enabled', false)
);

export const getContainerWideSelector = createSelector(getRelatedVideosFeatureSelector, (r) => r);

/**
 * @param {*} state
 * @return boolean
 */
export const withRatingSelector = createSelector(
  [displaySettingsSelector, getRatingFeatureSelector],
  (displaySettings, isRatingEnabled) => {
    if (!isNil(displaySettings)) {
      const rating = get(displaySettings, 'videoList.additionalData.rating', false);
      return rating && isRatingEnabled;
    }
    return false;
  }
);

/**
 * @param {*} state
 * @return boolean
 */
export const withPlaysSelector = createSelector(displaySettingsSelector, (displaySettings) => {
  return get(displaySettings, 'videoList.additionalData.plays', false);
});

/**
 * @param {*} state
 * @return boolean
 */
export const withUploadDateSelector = createSelector(displaySettingsSelector, (displaySettings) => {
  return get(displaySettings, 'videoList.additionalData.uploadDate', false);
});

export const withReportVideoSelector = createSelector(getFeaturesSelector, (features) =>
  get(features, 'ReportVideo.enabled', false)
);

export const isMyVideosVisibleSelector = createSelector(
  getUploadFeatureSelector,
  getUploadPodcastFeatureSelector,
  (getUploadFeature, getUploadPodcastFeature) => {
    const token = extractTokenPayload();
    if (!token) {
      return false;
    }

    return (getUploadFeature || getUploadPodcastFeature) && hasPermission(PermissionsEnum.UPLOAD_PERMISSION);
  }
);

export const isVideoUploadEnabled = isMyVideosVisibleSelector;
